import { useState } from "preact/compat";
import { useEffect } from "preact/hooks";
import { api } from "../../../services";
import { checkoutBeneficiaryFormValidator } from "../../../utils";
import BeneficiaryNormalizer from "../../../utils/BeneficiaryNormalizer";
import { hasNumber } from "./beneficiaryForm.helper";

export const useBeneficiaryForm = props => {
    // Destructured props
    const {
        beneficiary,
        setOpen,
        rehydrateBeneficiariesState,
        handleBeneficiary,
        stockItem,
        handleCurrentBeneficiaryChange,
        source,
        nadOrder,
        productId = undefined
    } = props;

    // State to store the initial values
    const [initialValues] = useState(() => {
        if (Object.keys(beneficiary).length > 0) {
            // Formats the beneficiary data model in case they're not yet formatted
            return beneficiary.dob ?
                BeneficiaryNormalizer(beneficiary) :
                beneficiary;
        }

        return {
            isBrazilian: true,
            address: "",
            birthdate: "",
            cpf: "",
            city: "",
            email: "",
            gender: "",
            name: "",
            country: "",
            number: "",
            passport: "",
            phone: "",
            postalCode: "",
            affiliation: "",
            // Parental fields
            responsibleName: "",
            responsibleEmail: "",
            responsibleTaxVat: "",
            responsiblePassport: "",
            // Social name feature
            includeSocialName: false,
            socialName: "",
        }
    });
    // State to store all the actual form values
    const [formValues, setFormValues] = useState(initialValues);
    // State to store all the eventual form errors
    const [errors, setErrors] = useState({});
    // State to flag whether is edit mode or not
    const [isEditMode, setIsEditMode] = useState(false);
    // State to control the form submission load state
    const [isLoading, setIsLoading] = useState(false);
    // State to check whether the beneficiary is an adult or not
    const [isUnderEighteen, setIsUnderEighteen] = useState(() => {
        // if (initialValues.birthdate) {
        //     return calculateYearsDifference(initialValues.birthdate) < 18;
        // }

        return false;
    });
    // Stores any eventual errors the request may return
    const [submitError, setSubmitError] = useState("");

    // Form validator methods
    const {
        validateForm,
        validateAll
    } = checkoutBeneficiaryFormValidator({
        formValues,
        isUnderEighteen
    });

    // Method to handle all the inputs when their values get changed
    const handleChange = e => {
        // Destructured props
        let { name, value, type } = e.target;

        if (name === "name" && hasNumber(value)) {
            value = value
                .replace(/\d+/g, '');
        }

        // Formats the birthdate field with the right regex
        if (name === "birthdate") {
            value = value
                .replace(/\D/g, "")
                .replace(/^(\d{2})(\d)/g, "$1/$2")
                .replace(/(\d)(\d{4})$/, "$1/$2");

            // TODO: for some still unknown reason, this feature isn't working properly,
            //  but it's not on the frontend side. We're still deactivating it for now, though.
            // if (value.length === 10) {
            //     setIsUnderEighteen(calculateYearsDifference(value) < 18);
            // } else if (value.length < 10 && isUnderEighteen) {
            //     setIsUnderEighteen(false);
            // }
        }

        // Formats the CPF field with the right regex
        if (name === "cpf" || name === "responsibleTaxVat") {
            value = value
                .replace(/\D/g, '')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1-$2')
                .replace(/(-\d{2})\d+?$/, '$1');
        }

        // Sets the values to the formValues state
        setFormValues(prevState => {
            return {
                ...prevState,
                [name]: (
                    type !== "checkbox" || type === "radio"
                ) ? value : e.target.checked
            }
        })

        // Validates the value during input time
        setErrors(prevState => {
            return {
                ...prevState,
                ...validateForm({
                    target: name,
                    value
                })
            }
        })
    }

    // Method to handle the form submission
    const handleSubmit = e => {
        // Prevents page from reloading when form is submitted
        e.preventDefault();

        // Validates all fields at once
        const validationResults = validateAll();

        // Appends the validated fields to the error state
        setErrors(validationResults);

        // Checks if there's any error in the form and, if so, abandons the method
        for (let i = 0; i < Object.values(validationResults).length; i++) {
            if (Object.values(validationResults)[i]) {
                return;
            }
        }

        // If validation is successful, then it does what it has to
        postData().then(() => { });
    }

    // Method to create a new beneficiary
    const postData = async () => {
        setSubmitError("");

        try {
            setIsLoading(true);

            // Payload to create a beneficiary
            const data = {
                name: formValues.name || "",
                gender: parseInt(formValues.gender, 10),
                taxvat: formValues.cpf,
                dob: formValues.birthdate,
                email: formValues.email,
                nationality: formValues.isBrazilian ? 1 : 0,
                sponsor: "",
                affiliation: formValues.affiliation,
                includeSocialName: formValues.includeSocialName,
                socialName: formValues.socialName,
                // Foreigner fields
                ...(!formValues.isBrazilian && {
                    passport: formValues.passport,
                    country: formValues.country,
                    phone: formValues.phone,
                    postalCode: formValues.postalCode,
                    city: formValues.city,
                    address: formValues.address,
                    number: formValues.number
                }),
                // Edit mode check
                ...(isEditMode && {
                    update: true,
                    entity_id: formValues.entityId
                }),
                // Edit mode check
                ...(isUnderEighteen && {
                    responsibleName: formValues.responsibleName,
                    responsibleEmail: formValues.responsibleEmail,
                    responsibleTaxVat: formValues.responsibleTaxVat,
                    responsiblePassport: formValues.responsiblePassport
                }),
                ...(productId && {
                    productId
                })
            }

            // Tries to perform the request to the backend
            let response = await api.post("/v1/beneficiary/save", data);

            // Checks if there was any untracked error during the request
            if (response.data.error) {
                if (response.data?.code === 1110) {
                    setSubmitError("Este CPF já está registrado para outro beneficiário.");
                } else {
                    setSubmitError("Erro ao enviar o formulário.");
                }

                return;
            }

            if (source !== "userLoggedInArea") {
                // Rehydrates the main beneficiaries state with the new one
                rehydrateBeneficiariesState({
                    data: response.data,
                    action: isEditMode ?
                        "update" :
                        source !== "default" ?
                            "create" :
                            undefined
                });

                // This is responsible for automatically setting the beneficiary to the product
                // after it has been successfully created
                handleBeneficiary({
                    ...(source === "default" && {
                        stockItem,
                    }),
                    beneficiaryId: parseInt(response.data.entityId, 10),
                    ...(source === "nad" && {
                        beneficiary: response.data,
                        order: nadOrder
                    }),
                });

                // Updates the button label with the name of the created beneficiary
                handleCurrentBeneficiaryChange({
                    beneficiaryName: (
                        response.data?.socialName || response.data?.name
                    ) || ""
                });
            } else {
                // Rehydrates the main beneficiaries state with the new one in a more simple way
                rehydrateBeneficiariesState(response.data);
            }

            setOpen(false);
        } catch (err) {
            console.error("Error while creating beneficiary:", err);

            // Checks if there was any untracked error during the request
            if (err.response.data.error) {
                if (err.response.data?.code === 1110) {
                    setSubmitError("Este CPF já está registrado para outro beneficiário.");
                } else {
                    setSubmitError("Erro ao enviar o formulário.");
                }
            }
        } finally {
            setIsLoading(false);
        }
    }

    // Performs the check for when it's in edit mode or not
    useEffect(() => {
        setIsEditMode(Object.keys(beneficiary).length > 0);
    }, [beneficiary]);

    useEffect(() => {
        // Validates all fields at once
        const validationResults = validateAll();

        // Appends the validated fields to the error state
        setErrors(validationResults);
    }, [formValues]);

    // FOR DEBUGGING PURPOSES ONLY
    // useEffect(() => {
    //     console.log("FORM VALUES", formValues);
    // }, [formValues]);

    return {
        formValues,
        errors,
        isLoading,
        isUnderEighteen,
        submitError,
        handleChange,
        handleSubmit,
    }
}
